import * as React from 'react';
import {History, Location} from 'history';
import styles from './ModuleMenu.module.css';
import {Module} from '../../../shared/models/Module';
import {Page} from '../../../shared/models/Page';
import {filter, partition} from 'lodash-es';
import ReactResizeDetector from 'react-resize-detector';
import {ThemeContext, ThemeContextProps} from '../../../app/context/ThemeContext';
import {isEmptyOrWhiteSpace} from '../../../shared/helpers/StateHelpers';
import {useCallback, useContext, useRef, useState} from 'react';
import {Navigator} from '../../context/Navigator';
import RenderHelpers from '../../../shared-ui/helpers/RenderHelpers';
import {ContextMenu} from '../../../shared-design-system/components/ContextMenu/ContextMenu';
import {Icon} from '../../../shared-design-system/components/Icon/Icon';

const MAX_VISIBLE = 6;

interface ModuleMenuProps {
  className?: string;
  modules: Module[];
  history: History;
  location: Location;
  onResize: (hasSufficientSpace: boolean) => void;
}

export const ModuleMenu = (props: ModuleMenuProps) => {
  const {navigateTo} = useContext(Navigator);
  const currentWidthRef = useRef<number>(undefined);
  const [moreAnchor, setMoreAnchor] = useState<HTMLElement | null>(null);
  const moreButtonRef = useRef<HTMLDivElement>(null);

  const splitModules = (modules: Module[]): { visible: Module[], overflow: Module[] } => {
    if (modules.length <= MAX_VISIBLE) {
      return {visible: modules, overflow: []};
    }
    const [low, normal] = partition(modules, m => m.lowerPriority);
    const ordered = [...normal, ...low];
    return {
      visible: ordered.slice(0, MAX_VISIBLE),
      overflow: ordered.slice(MAX_VISIBLE),
    };
  };

  const {visible, overflow} = splitModules(props.modules);
  const hasOverflow = overflow.length > 0;

  const onResize = (width: number) => {
    if (currentWidthRef.current !== width) {
      currentWidthRef.current = width;
      const itemCount = visible.length + (hasOverflow ? 1 : 0);
      const requiredWidth = itemCount * 96;
      const hasSufficientSpace = width + 12 >= requiredWidth;
      props.onResize(hasSufficientSpace);
    }
  };

  const isModuleActive = (module: Module): boolean => {
    const url = '/' + module.urlSlug;
    return props.history.location.pathname.split('/')[1] === url.split('/')[1];
  };

  const openExternalLink = (url: string) => {
    window.open(url, '_blank');
  };

  const findModuleExternalLinkUrl = (module: Module): string | undefined => {
    if (module.pages.length === 1) {
      const onlyPage = module.pages[0];
      if (onlyPage.isDefault && !isEmptyOrWhiteSpace(onlyPage.externalLinkUrl)) {
        return onlyPage.externalLinkUrl;
      }
    }
    return undefined;
  };

  const renderMenuItem = (module: Module,
                          index: number,
                          themeContext: ThemeContextProps) => {
    const url = '/' + module.urlSlug;
    const words = module.name.split(' ', 2);
    const firstWord = words[0];
    const secondWord = words[1];

    let activePages = filter<Page<any>>(module.pages, (page: Page<any>) => page.isVisible());

    let willShowPageMenu = (!activePages || activePages.length === 0 ||
      (activePages.length === 1 && activePages[0].isDefault));

    let isActive = isModuleActive(module);

    const moduleExternalLinkUrl = findModuleExternalLinkUrl(module);

    const tabContents = [
      <span
        key={0}
        style={{color: isActive ? themeContext.theme.mainMenu.primaryTextColor : ''}}
        className={styles.firstWord}
      >
          {(firstWord || '').toLowerCase()}
      </span>,
      <br key={1}/>,
      <span
        key={2}
        style={{color: isActive ? themeContext.theme.mainMenu.secondaryTextColor : ''}}
        className={styles.secondWord}
      >
      {(secondWord || '').toUpperCase()}
      </span>,
      <div
        key={3}
        className={willShowPageMenu ? styles.activeCaretWithPageMenu : styles.activeCaretWithoutPageMenu}
        style={{borderBottomColor: themeContext.theme.subMenu.primaryBackgroundColor}}
      />
    ];

    if (isEmptyOrWhiteSpace(moduleExternalLinkUrl)) {
      return (
        <div
          key={index}
          className={
            RenderHelpers.classes(
              'moduleMenuItem',
              isActive ? 'active' : undefined
            )
          }
          onClick={() => {
            navigateTo(url);
          }}
        >
          {tabContents}
        </div>
      );
    } else {
      return (<div
        key={index}
        onClick={() => openExternalLink(moduleExternalLinkUrl)}
        className={['moduleMenuItem', isActive ? 'active' : ''].join(' ')}
      >
        {tabContents}
      </div>);
    }
  };

  const isOverflowActive = overflow.some(isModuleActive);

  const handleMoreClick = useCallback(() => {
    if (moreAnchor) {
      setMoreAnchor(null);
    } else {
      setMoreAnchor(moreButtonRef.current);
    }
  }, [moreAnchor]);

  const handleMoreClose = useCallback(() => {
    setMoreAnchor(null);
  }, []);

  const renderMoreButton = (themeContext: ThemeContextProps) => {
    return (
      <div
        ref={moreButtonRef}
        key="more"
        className={RenderHelpers.classes(
          'moduleMenuItem',
          styles.moreButton,
          isOverflowActive ? 'active' : undefined,
          moreAnchor ? styles.moreButtonOpen : undefined
        )}
        onClick={handleMoreClick}
      >
        <span
          className={styles.firstWord}
          style={{color: isOverflowActive ? themeContext.theme.mainMenu.primaryTextColor : ''}}
        >
          more
        </span>
        <br/>
        <span
          className={RenderHelpers.classes(styles.secondWord, styles.moreCaret)}
        >
          <Icon
            glyph="expand"
            size={24}
            color={isOverflowActive ? themeContext.theme.mainMenu.secondaryTextColor : '#a6a6a6'}
          />
        </span>
      </div>
    );
  };

  const renderOverflowItem = (module: Module,
                               index: number,
                               themeContext: ThemeContextProps) => {
    const words = module.name.split(' ', 2);
    const firstWord = words[0];
    const secondWord = words[1];
    const isActive = isModuleActive(module);
    const moduleExternalLinkUrl = findModuleExternalLinkUrl(module);

    const handleClick = () => {
      handleMoreClose();
      if (!isEmptyOrWhiteSpace(moduleExternalLinkUrl)) {
        openExternalLink(moduleExternalLinkUrl);
      } else {
        navigateTo('/' + module.urlSlug);
      }
    };

    return (
      <div
        key={index}
        className={RenderHelpers.classes(
          styles.overflowMenuItem,
          isActive ? styles.overflowMenuItemActive : undefined
        )}
        onClick={handleClick}
      >
        <span
          className={styles.firstWord}
          style={{color: isActive ? themeContext.theme.mainMenu.primaryTextColor : ''}}
        >
          {(firstWord || '').toLowerCase()}
        </span>
        {' '}
        <span
          className={styles.secondWord}
          style={{color: isActive ? themeContext.theme.mainMenu.secondaryTextColor : ''}}
        >
          {(secondWord || '').toUpperCase()}
        </span>
      </div>
    );
  };

  return (
    <ThemeContext.Consumer>
      {themeContext => {
        return (
          <div className={[styles.menu, props.className].join(' ')}>
            <ReactResizeDetector
              handleWidth={true}
              onResize={onResize}
              refreshMode="debounce"
              refreshRate={500}
            />
            {visible.map((module: Module, index: number) =>
              renderMenuItem(module, index, themeContext)
            )}
            {hasOverflow && renderMoreButton(themeContext)}
            {moreAnchor && (
              <ContextMenu
                anchor={moreAnchor}
                placement="bottom-start"
                onClose={handleMoreClose}
              >
                <div
                  className={styles.overflowMenu}
                  style={{
                    background: `linear-gradient(${themeContext.theme.mainMenu.primaryBackgroundColor} 6%, ` +
                      `${themeContext.theme.mainMenu.secondaryBackgroundColor} 97%)`
                  }}
                >
                  {overflow.map((module: Module, index: number) =>
                    renderOverflowItem(module, index, themeContext)
                  )}
                </div>
              </ContextMenu>
            )}
          </div>
        );
      }}
    </ThemeContext.Consumer>
  );
};
